import { immer } from 'zustand/middleware/immer'

import { SomeEntity } from '~/clients/api-guards'

import type { Slice } from '../store'
import { walkEntities } from '../utils/walkEntities'
import { DiEntityKey, entityApiGuards, entityKeys, entityStates, entityTypeToKey } from './diSlice-entities.generated'
import { EntityTable } from './diSlice-utils'

export const ENTITY_TYPE = 'entity_type' as const
export type { DiEntityKey, DiEntityKeys } from './diSlice-entities.generated'
export { entityKeys } from './diSlice-entities.generated'

type EntityId = number

type State = {
    entities: typeof entityStates
}

type Actions = {
    actions: {
        addEntities: (entities: SomeEntity[]) => void
        removeEntity: (key: DiEntityKey, id: EntityId) => void
        removeEntities: (key: DiEntityKey, ids: EntityId[]) => void
        removeEntitiesByWebsocketEvent: (minimalEntities: SomeEntity[]) => void
        replaceEntity: (key: DiEntityKey, id: EntityId, entity: SomeEntity) => void
        replaceEntities: (key: DiEntityKey, entities: SomeEntity[]) => void
    }
}

export type DiSlice = {
    di: State & Actions
}

const reportedEntityTypesWithoutUuid = new Set<string>()

function reportEntityIfWithoutUuid(entity: SomeEntity) {
    if (!entity.uuid) {
        if (!reportedEntityTypesWithoutUuid.has(entity.entity_type)) {
            console.error('diSlice.addEntities: Entity type has no uuid', entity.entity_type)
            reportedEntityTypesWithoutUuid.add(entity.entity_type)
        }
    }
}

export const createDiSlice: Slice<DiSlice> = immer((set, get) => ({
    di: {
        entities: entityStates,
        actions: {
            addEntities: entities => {
                const updatedEntities: Partial<Record<DiEntityKey, Record<EntityId, SomeEntity>>> = {}
                const currentEntities = get().di.entities

                walkEntities(entities, entity => {
                    reportEntityIfWithoutUuid(entity)

                    const key = entityTypeToKey[entity.entity_type]
                    if (key && typeof entity.id === 'number') {
                        const current = currentEntities[key].byId[entity.id]
                        const entityUpdatedAt = entity.updated_at
                        const currentUpdatedAt = current?.updated_at
                        const isNewer = !current || !currentUpdatedAt || (entityUpdatedAt && entityUpdatedAt > currentUpdatedAt)

                        if (isNewer) {
                            let entityMap = updatedEntities[key]
                            if (!entityMap) {
                                entityMap = {}
                                updatedEntities[key] = entityMap
                            }
                            entityMap[entity.id] = entity
                        }
                    }
                })

                // Get keys and skip if no updates
                const keys = Object.keys(updatedEntities) as DiEntityKey[]
                if (keys.length === 0) return

                // Build new state outside of immer to avoid proxy overhead
                const currentState = get()
                const newEntities = { ...currentState.di.entities }
                const mutableEntities = newEntities as Record<DiEntityKey, EntityTable<SomeEntity>>

                for (const key of keys) {
                    const entityMap = updatedEntities[key]!
                    const currentById = currentState.di.entities[key].byId
                    const currentByUuid = currentState.di.entities[key].byUuid
                    const currentAllIds = currentState.di.entities[key].allIds
                    const currentAllUuids = currentState.di.entities[key].allUuids

                    const newById = { ...currentById }
                    const newByUuid = { ...currentByUuid }
                    const removedUuids = new Set<string>()
                    const newIds: number[] = []
                    const newUuids: string[] = []
                    for (const id in entityMap) {
                        const numId = Number(id)
                        const entity = entityMap[id] as (typeof newById)[number]
                        const previous = currentById[numId] as SomeEntity | undefined
                        newById[numId] = entity
                        newIds.push(numId)
                        const uuid = (entity as SomeEntity).uuid
                        const previousUuid = previous?.uuid
                        // Drop a stale uuid key when the entity either loses its uuid or
                        // gets a different one on refresh, so lookupEntity/selectByUuid
                        // don't keep resolving through the old key.
                        if (typeof previousUuid === 'string' && previousUuid !== uuid) {
                            delete newByUuid[previousUuid]
                            removedUuids.add(previousUuid)
                        }
                        if (typeof uuid === 'string') {
                            newByUuid[uuid] = entity
                            newUuids.push(uuid)
                        }
                    }

                    const allIdsSet = new Set(currentAllIds)
                    newIds.forEach(id => allIdsSet.add(id))
                    const allUuidsSet = new Set(currentAllUuids)
                    removedUuids.forEach(uuid => allUuidsSet.delete(uuid))
                    newUuids.forEach(uuid => allUuidsSet.add(uuid))

                    mutableEntities[key] = {
                        byId: newById,
                        allIds: Array.from(allIdsSet),
                        byUuid: newByUuid,
                        allUuids: Array.from(allUuidsSet),
                    }
                }

                // Use partial state update (bypasses immer mutations)
                set({ di: { ...currentState.di, entities: newEntities } })
            },
            removeEntity: (key, id) => {
                set(state => {
                    const existing = state.di.entities[key].byId[id]
                    if (existing) {
                        delete state.di.entities[key].byId[id]
                        const uuid = (existing as SomeEntity).uuid
                        if (typeof uuid === 'string' && state.di.entities[key].byUuid[uuid]) {
                            delete state.di.entities[key].byUuid[uuid]
                        }
                    }

                    state.di.entities[key].allIds = Object.keys(state.di.entities[key].byId).map(Number)
                    state.di.entities[key].allUuids = Object.keys(state.di.entities[key].byUuid)
                })
            },
            removeEntities: (key, ids) => {
                set(state => {
                    for (const id of ids) {
                        const existing = state.di.entities[key].byId[id]
                        if (existing) {
                            delete state.di.entities[key].byId[id]
                            const uuid = (existing as SomeEntity).uuid
                            if (typeof uuid === 'string' && state.di.entities[key].byUuid[uuid]) {
                                delete state.di.entities[key].byUuid[uuid]
                            }
                        }
                    }

                    state.di.entities[key].allIds = Object.keys(state.di.entities[key].byId).map(Number)
                    state.di.entities[key].allUuids = Object.keys(state.di.entities[key].byUuid)
                })
            },
            removeEntitiesByWebsocketEvent: (minimalEntities: SomeEntity[]) => {
                set(state => {
                    for (const entity of minimalEntities) {
                        reportEntityIfWithoutUuid(entity)

                        for (const key of entityKeys) {
                            if (entityApiGuards[key](entity)) {
                                const existing = state.di.entities[key].byId[entity.id]
                                if (existing) {
                                    delete state.di.entities[key].byId[entity.id]
                                    const existingUuid = (existing as SomeEntity).uuid
                                    if (typeof existingUuid === 'string' && state.di.entities[key].byUuid[existingUuid]) {
                                        delete state.di.entities[key].byUuid[existingUuid]
                                    }
                                }

                                state.di.entities[key].allIds = Object.keys(state.di.entities[key].byId).map(Number)
                                state.di.entities[key].allUuids = Object.keys(state.di.entities[key].byUuid)
                            }
                        }
                    }
                })
            },
            replaceEntity: (key, id, entity) => {
                set(state => {
                    const previous = state.di.entities[key].byId[id]
                    state.di.entities[key].byId[id] = entity as (typeof state.di.entities)[typeof key]['byId'][number]
                    if (!state.di.entities[key].allIds.includes(id)) {
                        state.di.entities[key].allIds.push(id)
                    }

                    const previousUuid = (previous as SomeEntity | undefined)?.uuid
                    const nextUuid = (entity as SomeEntity).uuid
                    if (typeof previousUuid === 'string' && previousUuid !== nextUuid && state.di.entities[key].byUuid[previousUuid]) {
                        delete state.di.entities[key].byUuid[previousUuid]
                        const idx = state.di.entities[key].allUuids.indexOf(previousUuid)
                        if (idx !== -1) state.di.entities[key].allUuids.splice(idx, 1)
                    }
                    if (typeof nextUuid === 'string') {
                        state.di.entities[key].byUuid[nextUuid] = entity as (typeof state.di.entities)[typeof key]['byUuid'][string]
                        if (!state.di.entities[key].allUuids.includes(nextUuid)) {
                            state.di.entities[key].allUuids.push(nextUuid)
                        }
                    }
                })
            },
            replaceEntities: (key, entities) => {
                set(state => {
                    const byId = state.di.entities[key].byId
                    const byUuid = state.di.entities[key].byUuid
                    const newIds: number[] = []
                    const newUuids: string[] = []

                    for (const entity of entities) {
                        const id = entity.id as number
                        const previous = byId[id]
                        byId[id] = entity as (typeof byId)[number]
                        newIds.push(id)

                        const previousUuid = (previous as SomeEntity | undefined)?.uuid
                        const nextUuid = entity.uuid
                        if (typeof previousUuid === 'string' && previousUuid !== nextUuid && byUuid[previousUuid]) {
                            delete byUuid[previousUuid]
                        }
                        if (typeof nextUuid === 'string') {
                            byUuid[nextUuid] = entity as (typeof byUuid)[string]
                            newUuids.push(nextUuid)
                        }
                    }

                    const existingIds = state.di.entities[key].allIds
                    const allIdsSet = new Set([...existingIds, ...newIds])
                    state.di.entities[key].allIds = Array.from(allIdsSet)
                    state.di.entities[key].allUuids = Object.keys(byUuid)
                })
            },
        },
    },
}))
